import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { validateIdentity } from '@/lib/auth/identity';

const K = 'dashboard.manager.dashboard.customers.addDialog';

export type CustomerFormValues = {
  firstNameAr: string;
  lastNameAr: string;
  phone: string;
  email: string;
};

function buildSchema(t: (k: string) => string) {
  return z
    .object({
      firstNameAr: z.string().min(2, 'First name must be at least 2 characters'),
      lastNameAr: z.string().min(2, 'Last name must be at least 2 characters'),
      phone: z.string().default(''),
      email: z.string().default(''),
    })
    .superRefine((v, ctx) => {
      const r = validateIdentity({ phone: v.phone, email: v.email });
      if (r.ok) return;
      const message =
        r.code === 'required' ? t(`${K}.phoneOrEmailRequired`)
        : r.code === 'phone-format' ? t(`${K}.phoneFormat`)
        : t(`${K}.emailFormat`);
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [r.field], message });
    });
}

interface CustomerDialogFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CustomerFormValues) => Promise<void>;
  isLoading: boolean;
  title: string;
}

export function CustomerDialogForm({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  title,
}: CustomerDialogFormProps) {
  const { t, dir } = useLanguage();
  const schema = useMemo(() => buildSchema(t), [t]);
  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstNameAr: '', lastNameAr: '', phone: '', email: '' },
  });

  const handleSubmit = async (values: CustomerFormValues) => {
    await onSubmit(values);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstNameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.dashboard.customers.addDialog.firstNameAr')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.firstNameAr')} disabled={isLoading} className="placeholder:text-gray-200" dir="rtl" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastNameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dashboard.manager.dashboard.customers.addDialog.lastNameAr')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('dashboard.manager.dashboard.customers.addDialog.placeholders.lastNameAr')} disabled={isLoading} className="placeholder:text-gray-200" dir="rtl" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`${K}.phone`)}</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder={t(`${K}.placeholders.phone`)} disabled={isLoading} className="placeholder:text-gray-200" maxLength={14} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t(`${K}.email`)}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" placeholder={t(`${K}.placeholders.email`)} disabled={isLoading} className="placeholder:text-gray-200" dir="ltr" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs text-muted-foreground">{t(`${K}.identityHint`)}</p>

            <div className={`flex justify-end gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
                {t('dashboard.manager.dashboard.customers.addDialog.cancel')}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('dashboard.manager.dashboard.customers.addDialog.adding')}
                  </>
                ) : (
                  t('dashboard.manager.dashboard.customers.addDialog.addCustomer')
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
